import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { BRAND } from '../brand/config';
import ko from './locales/ko.json';
import en from './locales/en.json';
import dappKo from './locales/dapp-ko.json';
import dappEn from './locales/dapp-en.json';
import guideFlowKo from './locales/guideFlow-ko.json';
import guideFlowEn from './locales/guideFlow-en.json';

const STORAGE_KEY = 'pingping_locale';

function mergeLocale(base, dapp, guideFlow) {
  return { ...base, ...dapp, guideFlow };
}

const MESSAGES = {
  ko: mergeLocale(ko, dappKo, guideFlowKo),
  en: mergeLocale(en, dappEn, guideFlowEn),
};

function getNested(obj, path) {
  return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

function interpolate(str, vars = {}) {
  if (typeof str !== 'string') return str;
  return str.replace(/\{\{(\w+)\}\}/g, (_, key) => (vars[key] !== undefined ? String(vars[key]) : ''));
}

function detectInitialLocale() {
  if (typeof window === 'undefined') return BRAND.defaultLocale;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && MESSAGES[stored]) return stored;
  const nav = (window.navigator.language || '').toLowerCase();
  if (nav.startsWith('ko')) return 'ko';
  if (nav.startsWith('en')) return 'en';
  return BRAND.defaultLocale;
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(detectInitialLocale);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (!MESSAGES[next]) return;
    setLocaleState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, next);
      document.documentElement.lang = next;
    }
  }, []);

  const t = useCallback(
    (key, vars) => {
      const raw = getNested(MESSAGES[locale], key) ?? getNested(MESSAGES.ko, key) ?? key;
      if (typeof raw !== 'string') return raw;
      return interpolate(raw, vars);
    },
    [locale],
  );

  const guideFlow = useMemo(
    () => MESSAGES[locale]?.guideFlow || MESSAGES.ko.guideFlow,
    [locale],
  );

  const value = useMemo(
    () => ({ locale, setLocale, t, guideFlow, productName: BRAND.productName }),
    [locale, setLocale, t, guideFlow],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

/** HTML `<strong>` 등이 포함된 마케팅 카피 */
export function RichText({ text, component: Component = 'span', ...props }) {
  if (!text || typeof text !== 'string') return null;
  const parts = text.split(/(<strong>.*?<\/strong>)/g);
  return (
    <Component {...props}>
      {parts.map((part, i) => {
        const m = part.match(/^<strong>(.*)<\/strong>$/);
        if (m) {
          return (
            <strong key={i}>
              {m[1]}
            </strong>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </Component>
  );
}
